import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Divider,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  CloudDownload as CloudDownloadIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface AnefImportModalProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess: (clientData: any) => void;
}

interface AnefData {
  nom?: string;
  prenom?: string;
  prenoms?: string;
  email?: string;
  adresse_mail?: string;
  telephone?: string;
  numero_telephone?: string;
  numero_passeport?: string;
  passeport?: {
    numero?: string;
    date_expiration?: string;
  };
  nationalite?: string;
  pays_naissance?: string;
  date_naissance?: string;
  lieu_naissance?: string;
  numero_etranger?: string;
}

const AnefImportModal: React.FC<AnefImportModalProps> = ({
  open,
  onClose,
  onImportSuccess,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [selectedDemarches, setSelectedDemarches] = useState<number[]>([]);

  const handleFetchData = async () => {
    if (!username || !password) {
      setError('Veuillez saisir les identifiants ANEF');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await api.post('/clients/anef/fetch', {
        username,
        password,
      });

      if (response.data.success) {
        setFetchedData(response.data.data);
        setSuccess(true);
      } else {
        setError(response.data.message || 'Erreur lors de la récupération des données ANEF');
      }
    } catch (err: any) {
      console.error('Erreur fetch ANEF:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors de la connexion au compte ANEF. Vérifiez les identifiants.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleImport = () => {
    if (fetchedData?.client_data) {
      // Récupérer les démarches sélectionnées
      const demarchesToImport = (fetchedData.demarches_data || []).filter(
        (_: any, index: number) => selectedDemarches.includes(index)
      );

      // Passer les données du client, identifiants ANEF ET démarches sélectionnées
      onImportSuccess({
        clientData: fetchedData.client_data,
        anefCredentials: fetchedData.anef_credentials,
        demarches: demarchesToImport
      });
      handleClose();
    }
  };

  const handleToggleDemarche = (index: number) => {
    setSelectedDemarches(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleToggleAllDemarches = () => {
    const demarchesData = fetchedData?.demarches_data || [];
    if (selectedDemarches.length === demarchesData.length) {
      setSelectedDemarches([]);
    } else {
      setSelectedDemarches(demarchesData.map((_: any, index: number) => index));
    }
  };

  const handleClose = () => {
    setUsername('');
    setPassword('');
    setError('');
    setSuccess(false);
    setFetchedData(null);
    setSelectedDemarches([]);
    onClose();
  };

  const anefData: AnefData = fetchedData?.anef_data || {};
  const clientData = fetchedData?.client_data || {};

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudDownloadIcon />
          <Typography variant="h6">Importer depuis un compte ANEF</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
            Données récupérées avec succès !
          </Alert>
        )}

        {!fetchedData && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Entrez les identifiants du compte ANEF du client pour récupérer automatiquement
              ses informations personnelles.
            </Alert>

            <TextField
              fullWidth
              label="Email / Nom d'utilisateur ANEF"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              autoFocus
            />

            <TextField
              fullWidth
              type="password"
              label="Mot de passe ANEF"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFetchData();
                }
              }}
            />
          </Box>
        )}

        {fetchedData && (
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Données récupérées
            </Typography>

            {/* Debug: afficher les données brutes */}
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
              <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem', overflow: 'auto', maxHeight: 200 }}>
                {JSON.stringify(fetchedData, null, 2)}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Nom
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {clientData.nom || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Prénom
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {clientData.prenom || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {clientData.email || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Téléphone
                  </Typography>
                  <Typography variant="body1">
                    {clientData.telephone_principal || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    N° Passeport
                  </Typography>
                  <Typography variant="body1">
                    {clientData.numero_passeport || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Nationalité / Pays
                  </Typography>
                  <Typography variant="body1">
                    {clientData.pays || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* Liste des démarches ANEF */}
            {fetchedData.demarches_data && fetchedData.demarches_data.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <AssignmentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Démarches en cours ({fetchedData.demarches_data.length})
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleToggleAllDemarches}
                    sx={{ textTransform: 'none' }}
                  >
                    {selectedDemarches.length === fetchedData.demarches_data.length
                      ? 'Tout désélectionner'
                      : 'Tout sélectionner'}
                  </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  Sélectionnez les démarches que vous souhaitez importer dans le système.
                </Alert>

                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {fetchedData.demarches_data.map((demarche: any, index: number) => (
                      <ListItem
                        key={index}
                        button
                        onClick={() => handleToggleDemarche(index)}
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: index < fetchedData.demarches_data.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedDemarches.includes(index)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {demarche.titre}
                              </Typography>
                              <Chip
                                label={demarche.statut}
                                size="small"
                                color={
                                  demarche.statut === 'en_cours' ? 'primary' :
                                  demarche.statut === 'termine' ? 'success' :
                                  demarche.statut === 'en_attente' ? 'warning' : 'default'
                                }
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {demarche.necessite_intervention && (
                                <Chip
                                  label="Intervention requise"
                                  size="small"
                                  color="error"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {demarche.description && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {demarche.description}
                                </Typography>
                              )}
                              {demarche.date_depot && (
                                <Typography variant="caption" color="text.secondary">
                                  Déposé le: {new Date(demarche.date_depot).toLocaleDateString('fr-FR')}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>

                {selectedDemarches.length > 0 && (
                  <Alert severity="success" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    {selectedDemarches.length} démarche{selectedDemarches.length > 1 ? 's' : ''} sélectionnée{selectedDemarches.length > 1 ? 's' : ''}
                  </Alert>
                )}
              </Box>
            )}

            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              Les informations seront pré-remplies dans le formulaire de création de client.
              Vous pourrez les modifier avant de sauvegarder.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>

        {!fetchedData ? (
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={loading || !username || !password}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
          >
            {loading ? 'Récupération...' : 'Récupérer les données'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleImport}
            startIcon={<CheckIcon />}
            color="success"
          >
            Importer et créer le client
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnefImportModal;
